import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTimepicker, MatTimepickerModule, MatTimepickerOption } from '@angular/material/timepicker';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { NumberOnlyDirective } from '@app/core/directives/number-only-directive';
import { CampaignDatesService } from '@campaign-form/form-fields/campaign-basics/campaign-dates/campaign-dates.service';
import { IGamePlayDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import { CustomCtaBaseDirective, ECustomFieldType } from '@core/directives/custom-cta-base.directive';
import { DurationPipe } from '@core/pipes/duration.pipe';
import { CustomValuesService, TFieldChangeTrackingContext } from '@core/services/custom-values.service';
import { ExecutionChangeTrackerService } from '@core/services/execution-change-tracker.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { CustomRemoveButtonComponent } from '@shared/components/custom-remove-button/custom-remove-button.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { ExecutionValidator } from '@shared/validators/execution-validator';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { FormUtilitiesService } from '@utilities/form-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { TimepickerUtilitiesService } from '@utilities/timepicker-utilities.service';
import { isAfter, isSameDay } from 'date-fns';
import { Subscription, take } from 'rxjs';
import { IExpectedKpiForm } from '../../../expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi/expected-kpi.interface';
import { EExecutionType } from './execution.interface';
import { ExecutionService } from './execution.service';

@Component({
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatErrorComponent,
    MatTimepickerModule,
    NumberOnlyDirective,
    MatCheckboxModule,
    MatTooltipModule,
    DatePipe,
    EditableTitleComponent,
    MatExpansionModule,
    MatSlideToggleModule,
    DurationPipe,
    NgClass,
    MatMenuModule,
    MatSelectModule,
    IconComponent,
    CustomRemoveButtonComponent,
    NgTemplateOutlet,
    IconBadgeComponent,
  ],
  providers: [provideNativeDateAdapter()],
  selector: 'app-execution',
  templateUrl: './execution.component.html',
})
export class ExecutionComponent extends CustomCtaBaseDirective implements OnInit, AfterViewInit {
  public readonly platformFeature = input.required<EPlatformFeature>();
  public readonly availableKpis = input.required<TExpectedKpi[] | null>();
  public readonly executionNumber = input<number>(1);
  public readonly gamePlayValues = input.required<FormArray<FormGroup<IGamePlayDeliverableForm>>['value']>();
  public readonly isSingleExecution = input<boolean>(true);

  public readonly executionDeleteCheckboxChange = output<MatCheckboxChange>();

  public readonly dateTooltip = viewChild<MatTooltip>('dateTooltip');
  public readonly timeTooltip = viewChild<MatTooltip>('timeTooltip');

  public readonly expectedKpisForm = computed(
    () => this.form().get('expectedKpis') as FormArray<FormGroup<IExpectedKpiForm>>,
  );
  public readonly executionId = computed(() => this.form().get('id')?.value ?? '');
  public readonly panelId = computed(() => `cta-execution-${this.executionId()}`);
  public readonly isNonStreamPlatformFeature = computed(() =>
    SocialUtilitiesService.isNonStreamPlatformFeature(this.platformFeature()),
  );

  public readonly expandedExecutionId = signal<string | null>(null);
  public readonly canDisplayTimeCustomValue = signal(true);
  public readonly isAllDeliverablesExpanded = signal(false);

  public readonly EExecutionType = EExecutionType;

  public get executionType(): FormControl<EExecutionType | null> {
    return this.form().get('executionType') as FormControl<EExecutionType | null>;
  }

  // Executions are their own FormArray (not a deliverable) - see event.interface.ts's executionId
  // field and custom-values.service.ts's IFieldChangeTrackingContext union.
  protected override get trackingContext(): TFieldChangeTrackingContext {
    return {
      getCtaTemplateId: (): string | undefined => this.ctaTemplateId(),
      getExecutionId: this.getExecutionId,
      kind: 'execution',
    };
  }

  public currentTimestamp: Date | null = null;

  public minDate: Date | null = null;
  public maxDate: Date | null = null;
  public minTime: Date | null = null;
  public maxTime: Date | null = null;

  public allTimeOptions: MatTimepickerOption<Date>[] = [];
  public filteredTimeOptions: MatTimepickerOption<Date>[] = [];

  protected readonly customValueConfig = {
    componentFields: {
      getForm: (): FormGroup => this.form(),
      managedFields: [
        { name: 'duration', type: ECustomFieldType.number },
        { name: 'executionDate', type: ECustomFieldType.date },
        { name: 'executionTime', type: ECustomFieldType.date },
        { name: 'executionCountTarget', type: ECustomFieldType.number },
        { name: 'id', type: ECustomFieldType.string },
      ],
    },
    nestedArray: {
      getFormArray: (): FormArray<FormGroup<IExpectedKpiForm>> => this.expectedKpisForm(),
      managedFields: [{ isMonitoringDisabled: true, name: 'expectedValue', type: ECustomFieldType.number }],
    },
  };

  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly campaignDatesService = inject(CampaignDatesService);
  private readonly timepickerUtilities = inject(TimepickerUtilitiesService);
  // eventTrackingService is inherited from CustomCtaBaseDirective (protected there).
  private readonly executionChangeTracker = inject(ExecutionChangeTrackerService);

  // executionType isn't in customValueConfig.componentFields (it's driven by the type-panel
  // expansion, not a plain input), so it can't go through CustomValuesService's generic
  // per-field change monitoring - tracked manually instead, see trackExecutionTypeChange().
  private readonly getExecutionId = (): string | undefined => this.form().get('id')?.value as string | undefined;
  private originalExecutionType: EExecutionType | null = null;
  private lastTrackedExecutionType: EExecutionType | null = null;

  private durationControlSubscription: Subscription | undefined;
  private executionTypeFormValues: Record<EExecutionType, FormGroup['value']> = {
    [EExecutionType.unique]: {
      executionType: EExecutionType.unique,
    },
    [EExecutionType.undesignated]: {
      executionType: EExecutionType.undesignated,
    },
    [EExecutionType.always]: {
      executionType: EExecutionType.always,
    },
  };

  constructor(customValuesService: CustomValuesService) {
    super(customValuesService);

    this.createExecutionTypeDisableEffect();
    this.createDurationControlEffect();
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.originalExecutionType = this.form().get('executionType')?.value ?? null;
    this.lastTrackedExecutionType = this.originalExecutionType;

    this.allTimeOptions = this.timepickerUtilities.generateTimeOptions();
    this.filteredTimeOptions = [...this.allTimeOptions];

    const executionDate = this.form().get('executionDate')?.value;
    const executionTime = this.form().get('executionTime')?.value;

    if (executionDate) {
      this.currentTimestamp = this.dateUtils.combineDateTime(executionDate, executionTime);
    }

    this.campaignDatesService.startDate$.pipe(take(1)).subscribe((startDate) => {
      if (startDate) {
        this.minDate = startDate;
        this.minTime = new Date(startDate);

        if (
          isAfter(this.form().get('executionDate')?.value, startDate) &&
          !isSameDay(this.form().get('executionDate')?.value, startDate)
        ) {
          this.minTime = null;
        }
      }

      this.updateFormValidators();
    });

    this.campaignDatesService.endDate$.pipe(take(1)).subscribe((endDate) => {
      if (endDate) {
        this.maxDate = endDate;
        this.maxTime = new Date(endDate);
      }

      this.updateFormValidators();
    });
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    if (this.isExpanded() && !this.isViewMode()) {
      FormUtilitiesService.scrollTo(`#${this.panelId()}`, 200);
    }
  }

  public onTimeInputChange(event: Event, timepicker: MatTimepicker<Date>): void {
    this.timepickerUtilities.handleTimePickerInputChange(
      event,
      timepicker,
      this.filteredTimeOptions,
      this.allTimeOptions,
    );

    if (this.form().get('executionTime')?.valid) {
      this.validateDate();
    }
  }

  public selectAllText(event: FocusEvent): void {
    const timeInput = event.target as HTMLInputElement;
    timeInput.select();
  }

  public onDateChange(event: { value: Date }): void {
    const selectedDate = event.value;

    if (this.minDate && this.maxDate) {
      const isMinAndMaxSameDay = this.minDate.toDateString() === this.maxDate.toDateString();
      const isMinDate = selectedDate.toDateString() === this.minDate.toDateString();
      const isMaxDate = selectedDate.toDateString() === this.maxDate.toDateString();
      const executionTimeHasCustomValue = this.hasCustomValue('executionTime');

      if (!executionTimeHasCustomValue) {
        this.canDisplayTimeCustomValue.set(false);
      }

      this.minTime = isMinAndMaxSameDay || isMinDate ? new Date(this.minDate) : null;
      this.maxTime = isMinAndMaxSameDay || isMaxDate ? new Date(this.maxDate) : null;

      setTimeout(() => {
        if (!executionTimeHasCustomValue) {
          this.onRevertToGlobalClick('executionTime', undefined, undefined);
        }

        this.canDisplayTimeCustomValue.set(true);
      });
    }

    const timestamp = this.dateUtils.combineDateTime(selectedDate, this.currentTimestamp || this.minTime, true);
    this.form().get('executionDate')?.setValue(timestamp);
    this.currentTimestamp = timestamp;

    if (!this.isViewMode()) {
      this.form().get('executionTime')?.enable({ emitEvent: false });
    }
  }

  public onTimeSelected(event: { value: Date }): void {
    requestAnimationFrame(() => {
      const currentDate = this.form().get('executionDate')?.value || this.minDate;
      const timestamp = this.dateUtils.combineDateTime(currentDate || event.value, event.value, true);
      this.form().get('executionTime')?.setValue(timestamp);

      this.validateDate();
    });
  }

  public clearDate(event: Event): void {
    event.stopPropagation();

    this.form().get('executionDate')?.setValue(null);
    this.handleExecutionTimeReset();

    this.currentTimestamp = null;
    this.dateTooltip()?.hide();
  }

  public clearTime(event: Event): void {
    event.stopPropagation();

    this.form().get('executionTime')?.setValue(null);
    this.currentTimestamp = this.form().get('executionDate')?.value;
    this.timeTooltip()?.hide();
  }

  public openDatePicker(datePicker: MatDatepicker<Date>): void {
    if (this.form().disabled) {
      return;
    }

    this.form().get('executionDate')?.markAsTouched();
    datePicker.open();
  }

  public openTimepicker(event: Event, timePicker: MatTimepicker<Date>): void {
    if (this.form().disabled || this.form().get('executionTime')?.disabled) {
      event.stopPropagation();

      return;
    }

    timePicker.open();
  }

  public onExecutionTypeChange(executionType: EExecutionType): void {
    switch (executionType) {
      case EExecutionType.unique:
        this.form().get('executionDate')?.setValidators([Validators.required]);
        this.form().get('executionDate')?.updateValueAndValidity();
        this.form().get('executionCountTarget')?.setValue(1);
        break;
      case EExecutionType.undesignated:
        this.form().get('executionDate')?.clearValidators();
        this.form().get('executionDate')?.updateValueAndValidity();
        this.form().get('executionDate')?.setValue(null);
        this.form().get('executionTime')?.setValue(null);
        break;
      case EExecutionType.always:
        this.form().get('duration')?.setValue(null);
        this.form().get('executionCountTarget')?.setValue(1);
        this.form().get('executionDate')?.clearValidators();
        this.form().get('executionDate')?.updateValueAndValidity();
        this.form().get('executionDate')?.setValue(null);
        this.form().get('executionTime')?.setValue(null);
        break;
    }

    this.form()
      .get('executionCountTarget')
      ?.setValidators(ExecutionService.getExecutionCountTargetValidators(executionType));
  }

  public onExecutionTypeExpansionChange(
    opened: boolean,
    executionType: EExecutionType,
    panel: MatExpansionPanel,
  ): void {
    if (this.isCustomMode() && !this.form().get('exclusiveTo')?.value) {
      panel.open();

      return;
    }

    this.updateExecutionTypeFormValues();

    if (opened) {
      this.form().patchValue(this.executionTypeFormValues[executionType]);
      this.onExecutionTypeChange(executionType);
      this.trackExecutionTypeChange(executionType);

      return;
    }

    if (executionType === this.form().get('executionType')?.getRawValue()) {
      this.form().get('executionType')?.setValue(null, { emitEvent: false });
      this.trackExecutionTypeChange(null);
    }
  }

  public override onRevertToGlobalClick(fieldName: string, index?: number, event?: Event): void {
    super.onRevertToGlobalClick(fieldName, index, event);
    this.dateTooltip()?.hide();
    this.timeTooltip()?.hide();

    if (!this.isCustomMode() || fieldName === 'duration') {
      return;
    }

    if (this.form().get('executionDate')?.value) {
      this.form().get('executionTime')?.enable({ emitEvent: false });
    } else if (this.hasCustomValue('executionTime')) {
      this.form().get('executionTime')?.disable({ emitEvent: false });
      this.onRevertToGlobalClick('executionTime', undefined, undefined);
    }
  }

  public override onDeleteCheckboxChange(fieldName: string, event: MatCheckboxChange): void {
    super.onDeleteCheckboxChange(fieldName, event);
    this.executionDeleteCheckboxChange.emit(event);
  }

  // The step's silently-created default row never goes through ExecutionListComponent.addExecution()
  // (which fires campaign_creation_execution_added immediately for the explicit "Add another"
  // button), so its added event is deferred to this - the first real user interaction with any new
  // execution - and fired lazily here if it hasn't already gone out. See
  // ExecutionChangeTrackerService's doc comment / event-tracking-implementation-map.md "Wave 9
  // plan" for the added-timing design.
  private trackExecutionTypeChange(newValue: EExecutionType | null): void {
    if (newValue === this.lastTrackedExecutionType) {
      return;
    }

    const executionId = this.getExecutionId();

    if (!this.executionChangeTracker.hasExecutionBeenAdded(executionId)) {
      this.executionChangeTracker.markExecutionAdded(executionId);

      this.eventTrackingService.track(EEventType.campaignCreationExecutionAdded, {
        campaignCreationCurrentStep: 3,
        campaignCreationIsOverride: this.isCustomMode(),
        creatorId: this.isCustomMode() ? (this.creatorId() ?? undefined) : undefined,
        ctaTemplateId: this.ctaTemplateId(),
        executionId,
      });
    }

    if (newValue === this.originalExecutionType) {
      this.executionChangeTracker.markFieldUnchanged(executionId, 'execution_type');
    } else {
      this.executionChangeTracker.markFieldChanged(executionId, 'execution_type');
    }

    this.eventTrackingService.track(EEventType.campaignCreationExecutionChanged, {
      campaignCreationCurrentStep: 3,
      campaignCreationIsOverride: this.isCustomMode(),
      creatorId: this.creatorId() ?? undefined,
      ctaTemplateId: this.ctaTemplateId(),
      executionId,
      newValue: newValue ?? undefined,
      previousValue: this.lastTrackedExecutionType ?? undefined,
      source: 'execution_type',
    });

    this.lastTrackedExecutionType = newValue;
  }

  private updateExecutionTypeFormValues(): void {
    const executionType = this.form().get('executionType')?.getRawValue() as EExecutionType;

    if (!executionType) {
      return;
    }

    this.executionTypeFormValues[executionType] = this.form().getRawValue();
  }

  private handleExecutionTimeReset(): void {
    const executionTimeControl = this.form().get('executionTime');

    executionTimeControl?.setValue(null);
    executionTimeControl?.setErrors(null);
    executionTimeControl?.disable({ emitEvent: false });

    if (this.isCustomMode() || this.hasCustomValue('executionDate')) {
      return;
    }

    this.onRevertToGlobalClick('executionTime', undefined, undefined);
  }

  private validateDate(): void {
    const executionDate = this.form().get('executionDate')?.value;

    if (!executionDate) {
      this.form().get('executionDate')?.setErrors({ dateRequiredWhenTimeIsSet: true });
    } else {
      this.form().get('executionDate')?.setErrors(null);
    }
  }

  private updateFormValidators(): void {
    const startDate = this.minDate;
    const endDate = this.maxDate;

    if (startDate && endDate) {
      this.form().setValidators([ExecutionValidator(startDate, endDate, this.dateUtils)]);
      this.form().updateValueAndValidity();
    }
  }

  private createExecutionTypeDisableEffect(): void {
    effect(() => {
      if ((this.isCustomMode() && this.form().get('exclusiveTo')?.value === null) || this.isViewMode()) {
        this.form().get('executionType')?.disable();
      } else {
        this.form().get('executionType')?.enable();
      }
    });
  }

  private createDurationControlEffect(): void {
    effect(() => {
      if (this.isViewMode()) {
        return;
      }

      const gamePlayDurations = this.gamePlayValues().reduce((acc, execution) => {
        const { duration } = execution;

        if (duration !== null) {
          acc.push(parseFloat(duration?.toString() ?? '0'));
        }

        return acc;
      }, [] as number[]);

      this.checkDuration(gamePlayDurations);
      this.durationControlSubscription?.unsubscribe();
      this.durationControlSubscription = this.form()
        ?.get('duration')
        ?.valueChanges?.subscribe(() => {
          this.checkDuration(gamePlayDurations);
        });
    });
  }

  private checkDuration(gamePlayDurations: number[]): void {
    const durationControl = this.form().get('duration');

    if (!durationControl) {
      return;
    }

    const executionDuration = parseFloat(durationControl.value);

    for (const duration of gamePlayDurations) {
      const isValid = executionDuration >= duration || !executionDuration;
      durationControl.setErrors(isValid ? null : { durationDoesNotFitGamePlays: true });

      if (!isValid) {
        break;
      }
    }
  }

  protected override afterModeChange(): void {
    const executionDate = this.form().get('executionDate')?.value;
    const executionTime = this.form().get('executionTime')?.value;

    this.currentTimestamp = executionDate ? this.dateUtils.combineDateTime(executionDate, executionTime) : null;

    if (this.form().get('executionDate')?.value && !this.isViewMode()) {
      this.form().get('executionTime')?.enable({ emitEvent: false });
    } else {
      this.form().get('executionTime')?.disable({ emitEvent: false });
    }
  }
}
